import { useCallback, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import { EditorState } from 'prosemirror-state';
import type { Editor, Extension, FocusPosition } from '@tiptap/react';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { BASE_EXTENSIONS } from './utils';

export type FormatType =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'bold'
  | 'bulletList'
  | 'orderedList'
  | 'link'
  | 'image';

type RichTextEditor = {
  editor: Editor | null;
  getHTML: () => string;
  getText: () => string;
  getCharacterCount: () => number;
  format: (formatType: FormatType) => void;
  focus: (focusPostion: FocusPosition) => void;
  isActive: (formatType: FormatType) => boolean | undefined;
  setContent: (content: string) => void;
  resetEditorContent: () => void;
};

interface Props {
  defaultContent?: string;
  placeholderText?: string | null;
  characterLimit?: number;
  onUpdate?: (editor: Editor) => void;
  onInit?: (editor: Editor) => void;
  onError?: (error: string) => void;
}

export const useRichTextEditor = ({
  defaultContent = '<p></p>',
  placeholderText = 'Start by typing something here',
  characterLimit,
  onUpdate,
  onInit,
  onError,
}: Props = {}): RichTextEditor => {
  const { current: extensions } = useRef(
    [
      History,
      ...BASE_EXTENSIONS,
      placeholderText &&
        typeof placeholderText === 'string' &&
        Placeholder.configure({
          includeChildren: true,
          showOnlyCurrent: false,
          placeholder: ({ pos, editor }) => {
            if (pos !== 0 || editor.getText().length > 0) return '';
            return placeholderText;
          },
        }),
      CharacterCount.configure({
        limit: characterLimit,
      }),
    ].filter(Boolean) as Extension[]
  );
  const editor = useEditor({
    onUpdate: ({ editor }) => {
      if (
        typeof characterLimit !== 'undefined' &&
        editor.storage.characterCount.characters() === characterLimit
      ) {
        onError?.(`Max ${characterLimit} character limit reached`);
      } else {
        onUpdate?.(editor as Editor);
      }
    },
    onCreate: ({ editor }) => {
      onInit?.(editor as Editor);
    },
    extensions,
    content: defaultContent,
  });

  const format = useCallback(
    (formatType: FormatType) => {
      switch (formatType) {
        case 'heading1':
          editor?.chain().focus().toggleHeading({ level: 3 }).run();
          break;
        case 'heading2':
          editor?.chain().focus().toggleHeading({ level: 4 }).run();
          break;
        case 'heading3':
          editor?.chain().focus().toggleHeading({ level: 5 }).run();
          break;
        case 'bold':
          editor?.chain().focus().toggleBold().run();
          break;
        case 'bulletList':
          editor?.chain().focus().toggleBulletList().run();
          break;
        case 'orderedList':
          editor?.chain().focus().toggleOrderedList().run();
          break;
        case 'image':
          const url = window.prompt('URL');

          if (url) {
            const isValidUrl = /^https:\/\/(images|media)\.simplywall\.st/.test(
              url
            );

            if (isValidUrl) {
              editor?.chain().focus().setImage({ src: url }).run();
            }
          }

          break;
      }
    },
    [editor]
  );

  const isActive = useCallback(
    (formatType: FormatType) => {
      switch (formatType) {
        case 'heading1':
          return editor?.isActive('heading', { level: 3 });
        case 'heading2':
          return editor?.isActive('heading', { level: 4 });
        case 'heading3':
          return editor?.isActive('heading', { level: 5 });
        case 'bold' || 'bulletList' || 'orderedList' || 'image' || 'link':
        default:
          return editor?.isActive(formatType);
      }
    },
    [editor]
  );

  const focus = useCallback(
    (focusPostion: FocusPosition) => editor?.commands.focus(focusPostion),
    [editor]
  );

  const getCharacterCount = useCallback(
    (): number => editor?.storage.characterCount.characters() ?? 0,
    [editor]
  );

  const getHTML = useCallback(() => editor?.getHTML() || '', [editor]);
  const getText = useCallback(() => editor?.getText() || '', [editor]);
  const setContent = useCallback(
    (content: string) => {
      if (editor) {
        editor.commands.setContent(content);
      }
    },
    [editor]
  );

  const resetEditorContent = useCallback(() => {
    if (editor) {
      editor.commands.setContent(defaultContent);
      const newEditorState = EditorState.create({
        doc: editor.state.doc,
        plugins: editor.state.plugins,
        schema: editor.state.schema,
      });
      editor.view.updateState(newEditorState);
    }
  }, [editor, defaultContent]);

  return {
    editor,
    getText,
    getHTML,
    focus,
    getCharacterCount,
    format,
    isActive,
    setContent,
    resetEditorContent,
  };
};
